import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import SectionHeader from "@/components/common/SectionHeader";

export default function ContactPage() {
  const [cfg, setCfg] = useState({
    business_email: "Aahakanakamagri.traders@gmail.com",
    business_phone: "+91 9502747531",
    business_whatsapp: "919848490057",
    business_whatsapp_secondary: "919502747531",
    business_address: "5-92 Veeramma Colony, Piduguralla, Guntur, Andhra Pradesh, India — 522413",
  });
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Contact — Aaha Kanakam Agri Traders";
    api.get("/site-config").then(({ data }) => setCfg((c) => ({ ...c, ...data }))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form });
      toast.success("Thank you. Your message has been received. We will get back to you shortly.");
      setForm({ full_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-forest text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-3">Contact</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">Tell us what you need. We will explore the right sourcing option for you.</h1>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <SectionHeader eyebrow="Get in touch" title="Business contact details" />
            <ContactItem icon={Mail} label="Email" value={cfg.business_email} href={`mailto:${cfg.business_email}`} testid="contact-email" />
            <ContactItem icon={Phone} label="Phone" value={cfg.business_phone} href={`tel:${cfg.business_phone.replace(/\s+/g,'')}`} testid="contact-phone" />
            <ContactItem icon={MessageCircle} label="WhatsApp (Primary)" value={`+${cfg.business_whatsapp}`} href={`https://wa.me/${cfg.business_whatsapp}`} testid="contact-whatsapp-primary" />
            {cfg.business_whatsapp_secondary && (
              <ContactItem icon={MessageCircle} label="WhatsApp (Alternate)" value={`+${cfg.business_whatsapp_secondary}`} href={`https://wa.me/${cfg.business_whatsapp_secondary}`} testid="contact-whatsapp-secondary" />
            )}
            <ContactItem icon={MapPin} label="Office" value={cfg.business_address} testid="contact-address" />
            <ContactItem icon={Clock} label="Business Hours" value="Mon – Sat, 9:30 AM – 6:30 PM IST" testid="contact-hours" />
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-borderSubtle p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *">
                  <input data-testid="contact-input-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Email *">
                  <input type="email" data-testid="contact-input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Phone / WhatsApp" full>
                  <input data-testid="contact-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Your Message *" full>
                  <textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <button data-testid="contact-submit" disabled={submitting} className="btn-gold px-7 py-3 text-sm font-semibold tracking-wide disabled:opacity-60">
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full px-4 py-3 text-sm bg-white border border-borderSubtle focus:outline-none focus:border-forest";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-charcoal font-semibold block mb-2">{label}</span>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const body = (
    <div className="flex items-start gap-4 p-5 bg-white border border-borderSubtle card-hover">
      <div className="w-11 h-11 bg-surface text-forest flex items-center justify-center shrink-0"><Icon size={18} /></div>
      <div>
        <div className="eyebrow mb-1">{label}</div>
        <div className="text-sm text-charcoal font-medium break-words">{value}</div>
      </div>
    </div>
  );
  return href ? <a data-testid={testid} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{body}</a> : <div data-testid={testid}>{body}</div>;
}
